import React from 'react';
import { injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  isPlainObject,
  first,
  isEmpty,
} from 'lodash';
import { reduxForm } from 'redux-form';

import {
  moreThan180Days,
} from '../../../components/Time';
import Purchase from '../../../lib/purchase';
import moment from 'moment';

import { formatMessage } from '../../lib/intl';
import { format } from '../../lib/money';
import { SPECIFIED_PER_PERSON, EVEN_SPLIT } from '../../lib/split-types';
import {
  ActivityComment,
  ActivityCard,
  PURCHASE_UPDATED,
  PURCHASE_CLAIMED,
  MEMBER_INVITED,
  MEMBER_JOINED,
  MEMBER_WITHDREW,
  PURCHASE_TIPPED,
  PURCHASE_UNTIPPED,
  PAYMENT_AUTHORIZED,
  PAYMENT_AUTHORIZED_DEPOSIT,
  PAYMENT_AUTHORIZED_REGULAR,
  PAYMENT_AUTHORIZED_LAST,
  PAYMENT_CAPTURED,
  PURCHASE_COMPLETED,
  PURCHASE_COMMENT_CREATED,
  AddComment,
  Banner,
  PrimaryButton,
  Subheader,
} from '../../components/pbg-ui-library';

import { i18n } from './activities.i18n';

const formatDatetime = (time, tz = null) => {
  if (tz) {
    if (moreThan180Days(time)) return moment(time).tz(tz).format('DD MMM YYYY hh:mma z');
    return moment(time).tz(tz).format('DD MMM hh:mma z')
  }

  if (moreThan180Days(time)) return moment(time).format('DD MMM YYYY hh:mma');
  return moment(time).format('DD MMM hh:mma')
}

export const formatDatetimeShort = (time) => {
  if (moreThan180Days(time)) return moment(time).format('DD MMM YYYY');
  return moment(time).format('DD MMM')
}

export class Activities extends React.PureComponent {
  state = {
    commentText: null,
    error: null,
  }
  static propTypes = {
    purchase: PropTypes.object,
    memberships: PropTypes.array,
    organizerUser: PropTypes.object,
  }

  static defaultProps = {
    purchase: {},
    memberships: [],
    organizerUser: {},
  }

  formatMessage = formatMessage(this.props.intl);

  isEmptyProductDescription(key, value) {
    return key === 'productDescription' && isEmpty(value);
  }

  getOrgOMerchantFullName(activity) {
    const { purchase } = this.props;

    let fullName = this.props.organizerUser.fullName;

    if (activity.data.updatedBy === "merchant") {
      fullName = purchase.merchant.displayName;
    }

    return fullName;
  }

  userDisplayName(user) {
    return user.fullName || `${user.email.split('@')[0]}@...`
  }

  onSubmit = () => {
    const { commentText } = this.state;

    if (!commentText) {
      this.setState({
        error: this.formatMessage(i18n.addCommentError),
      });
      return false;
    }

    this.props.onPostComment(commentText)
      .then(() => {
        this.setState({
          commentText: null,
        });
      });
  }

  onSignupClick = () => {
    window.$('#signup').modal('show');
  }

  formatPurchaseUpdatedAttrValue(key, data) {
    let value = data[key];

    if (key === 'productDescription') return false;

    if (isPlainObject(data[key]) && data[key].hasOwnProperty('amountCents')) {
      value = format(data[key]);
    }

    if (moment(value, moment.ISO_8601, true).isValid()) {
      value = moment(value).local().format('ll ha z');
    }

    if (value === SPECIFIED_PER_PERSON) {
      value = this.formatMessage(i18n.ssp);
    }

    if (value === EVEN_SPLIT) {
      value = this.formatMessage(i18n.even);
    }

    if (key === 'productName') {
      value = `"${value}"`;
    }

    return value;
  }

  renderPurchaseUpdatedItem({ activity, key, data }) {
    const fullName = this.getOrgOMerchantFullName(activity);
    const attribute = this.formatMessage(i18n[key]);
    const value = this.formatPurchaseUpdatedAttrValue(key, data);

    if (!value) {
      if (!data[key] || this.isEmptyProductDescription(key, data[key])) {
        return (
          <p>{
            this.formatMessage(i18n.purchaseUpdatedDescriptionNoValueSpecial, {
              fullName,
              attribute,
            })
          }</p>
        );
      }

      return (
        <p>{
          this.formatMessage(i18n.purchaseUpdatedDescriptionNoValue, {
            fullName,
            attribute,
          })
        }</p>
      );
    }

    return (
      <p>{
        this.formatMessage(i18n.purchaseUpdatedDescription, {
          fullName,
          attribute,
          value,
        })
      }</p>
    );
  }

  renderSingleProductCost(activity, cost) {
    const { purchase } = this.props;
    const { minSlots, maxSlots, amount } = cost;

    let fullName = this.props.organizerUser.fullName;

    if (activity.data.updatedBy === "merchant") {
      fullName = purchase.merchant.displayName;
    }

    if ((!minSlots || minSlots === 1) && !maxSlots) {
      return (
        <p>
          {this.formatMessage(i18n.purchaseUpdatedDescription, {
            fullName,
            attribute: this.formatMessage(i18n.productCosts),
            value: format(amount),
          })}
        </p>
      );
    }

    if (minSlots && maxSlots) {
      return (
        <p>
          {this.formatMessage(i18n.purchaseUpdatedDescriptionTotalCost, {
            fullName,
            attribute: this.formatMessage(i18n.productCosts),
            minSlots,
            maxSlots,
            amount: format(amount),
          }, purchase.slotName)}
        </p>
      );
    }

    if (minSlots > 1 && !maxSlots) {
      return (
        <p>
          {this.formatMessage(i18n.purchaseUpdatedDescriptionTotalCostNoMax, {
            fullName,
            attribute: this.formatMessage(i18n.productCosts),
            minSlots,
            amount: format(amount),
          }, purchase.slotName)}
        </p>
      );
    }
  }

  renderSingleProductCostMulti(activity, cost) {
    const { purchase } = this.props;
    const { minSlots, maxSlots, amount } = cost;

    if ((!minSlots || minSlots === 1) && !maxSlots) {
      return (
        <p>
          {this.formatMessage(i18n.purchaseUpdatedDescriptionMulti, {
            attribute: this.formatMessage(i18n.productCosts),
            value: format(amount),
          })}
        </p>
      );
    }

    if (minSlots && maxSlots) {
      return (
        <p>
          {this.formatMessage(i18n.purchaseUpdatedDescriptionTotalCostMulti, {
            attribute: this.formatMessage(i18n.productCosts),
            minSlots,
            maxSlots,
            amount: format(amount),
          }, purchase.slotName)}
        </p>
      );
    }

    if (minSlots > 1 && !maxSlots) {
      return (
        <p>
          {this.formatMessage(i18n.purchaseUpdatedDescriptionTotalCostNoMaxMulti, {
            attribute: this.formatMessage(i18n.productCosts),
            minSlots,
            amount: format(amount),
          }, purchase.slotName)}
        </p>
      );
    }
  }

  renderProductCosts({ activity, key, data }) {
    const { purchase } = this.props;

    let fullName = this.props.organizerUser.fullName;

    if (activity.data.updatedBy === "merchant") {
      fullName = purchase.merchant.displayName;
    }

    const costs = data[key];

    if (costs.length === 1) {
      return this.renderSingleProductCost(activity, first(costs));
    }

    const costElements = this.renderCostElements(costs);

    return (
      <React.Fragment>
        <p className="mb-0">
          {this.formatMessage(i18n.purchaseUpdatedProductCostsDescription, {
            fullName,
          })}
        </p>
        <ul>
          {costElements}
        </ul>
      </React.Fragment>
    );
  }

  renderProductPayments({ activity, key, data }) {
    const { purchase, organizerUser } = this.props;

    let fullName = organizerUser.fullName;

    if (activity.data.updatedBy === "merchant") {
      fullName = purchase.merchant.displayName;
    }

    const attribute = this.formatMessage(i18n[key]);

    const payments = data[key];

    if (payments.length === 1) {
      if (!payments[0].dueDeadline) {
        return (
          <p>
            {this.formatMessage(i18n.purchaseUpdatedSinglePaymentNoDueDeadlineSingle, {
              fullName,
            })}
          </p>
        );
      };

      return (
        <p>
          {this.formatMessage(i18n.purchaseUpdatedDescription, {
            fullName,
            attribute,
            value: formatDatetime(payments[0].dueDeadline, payments[0].dueDeadlineTimezone),
          })}
        </p>
      )
    }

    const paymentElements = payments.map((payment) => {
      const { number, dueDeadline, dueDeadlineTimezone, percentage } = payment;
      if (!dueDeadline) {
        return (
          <li className="ml-5" key={number}>
            {this.formatMessage(i18n.purchaseUpdatedPaymentNoDuedeadline, {
              number: number,
              percentage: parseFloat(percentage),
            })}
          </li>
        );
      }

      return (
        <li className="ml-5" key={number}>
          {this.formatMessage(i18n.purchaseUpdatedPayment, {
            number: number,
            percentage: parseFloat(percentage),
            date: formatDatetime(dueDeadline, dueDeadlineTimezone),
          })}
        </li>
      )
    });

    return (
      <React.Fragment>
        <p className="mb-0">
          {this.formatMessage(i18n.purchaseUpdatedPaymentDescription, {
            fullName,
          })}
        </p>
        <ul>
          {paymentElements}
        </ul>
      </React.Fragment>
    );
  }

  renderProductDatetime({ activity, key, data }) {
    const { purchase } = this.props;

    let fullName = this.props.organizerUser.fullName;

    if (activity.data.updatedBy === "merchant") {
      fullName = purchase.merchant.displayName;
    }

    const attribute = this.formatMessage(i18n[key]);
    let timezoneAttr = key === 'productStartDatetime' ?
      'productStartDatetimeTimezone' : 'productEndDatetimeTimezone';

    return (
      <p>
        {this.formatMessage(i18n.purchaseUpdatedDescription, {
          fullName,
          attribute,
          value: formatDatetime(data[key], data[timezoneAttr]),
        })}
      </p>
    )
  }

  renderPurchaseUpdatedItemMulti({ key, data }) {
    const attribute = this.formatMessage(i18n[key]);
    let value = this.formatPurchaseUpdatedAttrValue(key, data);

    if (!value) {
      if (!data[key] || this.isEmptyProductDescription(key, data[key])) {
        return (
          <p>{
            this.formatMessage(i18n.purchaseUpdatedDescriptionNoValueMultiSpecial, {
              attribute,
            })
          }</p>
        );
      }

      return (<p>{
        this.formatMessage(i18n.purchaseUpdatedDescriptionNoValueMulti, {
          attribute,
        })
      }</p>);
    }

    return (<p>{
      this.formatMessage(i18n.purchaseUpdatedDescriptionMulti, {
        attribute, value,
      })
    }</p>);
  }

  renderCostElements(costs = []) {
    const { purchase } = this.props;
    return costs.map((cost) => {
      const { minSlots, maxSlots, amount } = cost;

      if (!maxSlots) {
        return (
          <li className="ml-5" key={cost.number}>
            {this.formatMessage(i18n.purchaseUpdatedProductCostsNoMaxSlots, {
              minSlots,
              amount: format(amount),
            }, purchase.slotName)}
          </li>
        );
      }

      return (
        <li className="ml-5" key={cost.number}>
          {this.formatMessage(i18n.purchaseUpdatedProductCosts, {
            minSlots,
            maxSlots,
            amount: format(amount),
          }, purchase.slotName)}
        </li>
      )
    });
  }

  renderProductCostsMulti({ activity, key, data }) {

    const costs = data[key];

    if (costs.length === 1) {
      return this.renderSingleProductCostMulti(activity, first(costs));
    }

    const costElements = this.renderCostElements(costs);

    return (<React.Fragment>
      <p className="mb-0">
        {this.formatMessage(i18n.purchaseUpdatedProductCostsDescriptionMulti)}
      </p>
      <ul>
        {costElements}
      </ul>
    </React.Fragment>);
  }

  renderPaymentElements(payments) {
    return payments.map((payment) => {
      const { number, dueDeadline, dueDeadlineTimezone, percentage } = payment;

      if (!dueDeadline) {
        return (
          <li className="ml-5" key={number}>
            {this.formatMessage(i18n.purchaseUpdatedPaymentNoDuedeadline, {
              number: number,
              percentage: parseFloat(percentage),
            })}
          </li>
        );
      }

      return (
        <li className="ml-5" key={number}>
          {this.formatMessage(i18n.purchaseUpdatedPayment, {
            number: number,
            percentage: parseFloat(percentage),
            date: formatDatetime(dueDeadline, dueDeadlineTimezone),
          })}
        </li>
      )
    });
  }

  renderProductPaymentsMulti({ key, data }) {
    const attribute = this.formatMessage(i18n[key]);
    const payments = data[key];

    if (payments.length === 1) {
      if (!payments[0].dueDeadline) {
        return (
          <p>
            {this.formatMessage(i18n.purchaseUpdatedSinglePaymentNoDueDeadlineMulti)}
          </p>
        );
      };

      return (<p>
        {this.formatMessage(i18n.purchaseUpdatedDescriptionMulti, {
          attribute,
          value: formatDatetime(payments[0].dueDeadline, payments[0].dueDeadlineTimezone),
        })}
      </p>)
    }

    const paymentElements = this.renderPaymentElements(payments);

    return (<React.Fragment>
      <p className="mb-0">
        {this.formatMessage(i18n.purchaseUpdatedPaymentDescriptionMulti)}
      </p>
      <ul>
        {paymentElements}
      </ul>
    </React.Fragment>);
  }

  renderProductDatetimeMulti({ key, data }) {
    const attribute = this.formatMessage(i18n[key]);
    let timezoneAttr = key === 'productStartDatetime' ?
      'productStartDatetimeTimezone' : 'productEndDatetimeTimezone';

    if (!data[key]) {
      return (
        <p>{
          this.formatMessage(i18n.purchaseUpdatedDescriptionNoValueMultiSpecial, {
            attribute,
          })
        }</p>
      );
    }

    return (
      <p>
        {this.formatMessage(i18n.purchaseUpdatedDescriptionMulti, {
          attribute,
          value: formatDatetime(data[key], data[timezoneAttr]),
        })}
      </p>
    )
  }

  renderPurchaseUpdatedWithSingleAttribute(activity) {
    const data = { ...activity.attributes };

    const description = Object.keys(data).map((k) => {
      const config = { activity, key: k, data };

      let result;

      switch (k) {
        case 'productStartDatetimeTimezone':
        case 'productEndDatetimeTimezone':
          break;
        case 'productCosts':
          result = this.renderProductCosts(config);
          break;
        case 'payments':
          result = this.renderProductPayments(config);
          break;
        case 'productStartDatetime':
        case 'productEndDatetime':
          result = this.renderProductDatetime(config);
          break;
        case 'productLink':
          result = this.renderProductLinkMulti(config);
          break;
        default:
          result = this.renderPurchaseUpdatedItem(config);
          break;
      }

      return (
        <div key={k}>
          {result}
        </div>
      );
    });

    return (
      <ActivityCard
        key={activity.id}
        type={activity.type}
        title={this.formatMessage(i18n.purchaseUpdatedTitle)}
        time={activity.occurredAt}
      >{description}</ActivityCard>
    );
  }

  renderProductLink({ activity, key, data }) {
    const fullName = this.getOrgOMerchantFullName(activity);
    const attribute = this.formatMessage(i18n[key]);
    const value = this.formatPurchaseUpdatedAttrValue(key, data);

    if (!value) {
      return (
        <p>{
          this.formatMessage(i18n.purchaseUpdatedLinkNoValue, {
            fullName,
            attribute,
          })
        }</p>
      );
    }

    return (
      <p>{
        this.formatMessage(i18n.purchaseUpdatedLink, {
          attribute,
        })
        }
        {' '}
        <a href={value} target="_blank">
          {this.formatMessage(i18n.purchaseUpdatedLinkThisPage)}
        </a>
      </p>
    );
  }

  renderProductLinkMulti({ activity, key, data }) {
    // const fullName = this.getOrgOMerchantFullName(activity);
    const attribute = this.formatMessage(i18n[key]);
    const value = this.formatPurchaseUpdatedAttrValue(key, data);


    if (!value) {
      return (
        <p>{
          this.formatMessage(i18n.purchaseUpdatedLinkMultiNoValue, {
            attribute,
          })
        }</p>
      );
    }

    return (
      <p>{
        this.formatMessage(i18n.purchaseUpdatedLinkMulti, {
          attribute,
        })
        }
        {' '}
        <a href={value} target="_blank">
          {this.formatMessage(i18n.purchaseUpdatedLinkThisPage)}
        </a>
      </p>
    );
  }

  renderPurchaseUpdated(activity) {
    const data = { ...activity.attributes };

    const dataLength = Object.keys(data).length;

    if (!dataLength) return false;

    // If single attribute was updated
    if (dataLength === 1) {
      return this.renderPurchaseUpdatedWithSingleAttribute(activity);
    }

    // If multiple attributes were updated;
    const { purchase, organizerUser } = this.props;

    let fullName = organizerUser.fullName;

    if (activity.data.updatedBy === "merchant") {
      fullName = purchase.merchant.displayName;
    }

    const descriptionTitle = this.formatMessage(i18n.purchaseUpdatedDescriptionMultiTitle, {
      fullName,
    });

    const description = Object.keys(data).map((k) => {
      const config = { activity, key: k, data };

      let result;

      switch (k) {
        case 'productStartDatetimeTimezone':
        case 'productEndDatetimeTimezone':
          break;
        case 'productCosts':
          result = this.renderProductCostsMulti(config);
          break;
        case 'payments':
          result = this.renderProductPaymentsMulti(config);
          break;
        case 'productStartDatetime':
        case 'productEndDatetime':
          result = this.renderProductDatetimeMulti(config);
          break;
        case 'productLink':
          result = this.renderProductLinkMulti(config);
          break;
        default:
          result = this.renderPurchaseUpdatedItemMulti(config);
          break;
      }

      return (
        <div key={k}>
          {result}
        </div>
      );
    });

    return (
      <ActivityCard
        key={activity.id}
        type={activity.type}
        title={this.formatMessage(i18n.purchaseUpdatedTitle)}
        time={activity.occurredAt}
      >
        <p className="activity-card-description-title">{descriptionTitle}</p>
        {description}
      </ActivityCard>
    );
  }

  renderMemberWithdrew(activity) {
    const user = {
      fullName: activity.data.memberUserFullName,
      email: activity.data.memberUserEmail,
    }

    const description = this.formatMessage(i18n.memberWithdrewDescription, {
      fullName: this.userDisplayName(user),
    });

    return <ActivityCard
      key={activity.id}
      type={activity.type}
      title={this.formatMessage(i18n.memberWithdrewTitle)}
      time={activity.occurredAt}
    ><p>{description}</p></ActivityCard>
  }

  renderPurchaseClaimed(activity) {
    const { organizerUser } = this.props;

    const description = this.formatMessage(i18n.purchaseClaimedDescription, {
      fullName: this.userDisplayName(organizerUser),
    });

    return <ActivityCard
      key={activity.id}
      type={activity.type}
      title={this.formatMessage(i18n.purchaseClaimedTitle)}
      time={activity.occurredAt}
    ><p>{description}</p></ActivityCard>
  }

  getPurchaseTippedDescription(activity) {
    const { purchase } = this.props;
    const { data: { groupSplitType } } = activity;

    if (groupSplitType === SPECIFIED_PER_PERSON) {
      return Purchase.isSinglePayment(purchase.payments) ?
        this.formatMessage(i18n.purchaseTippedDescriptionSPPSingle)
        :
        this.formatMessage(i18n.purchaseTippedDescriptionSPPMulti);
    }


    return Purchase.isSinglePayment(purchase.payments) ?
      this.formatMessage(i18n.purchaseTippedDescription)
      :
      this.formatMessage(i18n.purchaseTippedDescriptionMulti);
  }

  renderPurchaseTipped(activity) {
    const title = this.formatMessage(i18n.purchaseTippedTitle);

    const description = this.getPurchaseTippedDescription(activity);

    return <ActivityCard
      key={activity.id}
      type={activity.type}
      title={title}
      time={activity.occurredAt}
    ><p>{description}</p></ActivityCard>
  }

  renderPurchaseUntipped(activity) {
    const description = this.formatMessage(i18n.purchaseUntippedDescription);

    return <ActivityCard
      key={activity.id}
      type={activity.type}
      title={this.formatMessage(i18n.purchaseUntippedTitle)}
      time={activity.occurredAt}
    ><p>{description}</p></ActivityCard>
  }

  renderPurchaseCompleted(activity) {
    const { data: { paymentAmount, totalOfPayments } } = activity;
    const {
      purchase: {
        merchant: { displayName },
      },
    } = this.props;

    const config = {
      paymentAmount: format(paymentAmount),
      merchantDisplayName: displayName,
    };

    const description = totalOfPayments === 1 ?
      this.formatMessage(i18n.paymentCapturedDescriptionSingle, config)
      :
      this.formatMessage(i18n.paymentCapturedDescriptionMulti, config);

    return <ActivityCard
      key={activity.id}
      type={activity.type}
      title={this.formatMessage(i18n.purchaseCompletedTitle)}
      time={activity.occurredAt}
    ><p>{description}</p></ActivityCard>
  }

  renderPaymentCaptured(activity) {
    const { purchase: { merchant } } = this.props;
    const { data: { paymentType } } = activity;

    const config = {
      merchantDisplayName: merchant.displayName,
      paymentNumber: activity.data.paymentNumber,
      totalOfPayments: activity.data.totalOfPayments,
      paymentAmount: format(activity.data.paymentAmount),
    };

    let description = this.formatMessage(i18n.paymentCapturedDescription, config);

    if (paymentType === 'deposit') {
      description = this.formatMessage(i18n.paymentCapturedDescriptionDeposit, config);
    }

    return <ActivityCard
      key={activity.id}
      type={activity.type}
      title={this.formatMessage(i18n.paymentCapturedTitle)}
      time={activity.occurredAt}
    ><p>{description}</p></ActivityCard>
  }

  renderPaymentAuthorized(activity) {
    switch (activity.data.paymentType) {
      case "deposit":
        return this.renderDepositAuthorized(activity);
      case "regular":
        return this.renderRegularPaymentAuthorized(activity);
      case "last":
        return this.renderLastPaymentAuthorized(activity);
      default:
        return false
    }
  }

  renderDepositAuthorized(activity) {
    const { purchase: { merchant }, organizerUser } = this.props;

    if (!merchant) return false;

    const description = this.formatMessage(i18n.depositAuthorizedDescription, {
      merchantDisplayName: merchant.displayName,
      fullName: this.userDisplayName(organizerUser),
    });

    return <ActivityCard
      key={activity.id}
      type={activity.type}
      title={this.formatMessage(i18n.depositAuthorizedTitle)}
      time={activity.occurredAt}
    ><p>{description}</p></ActivityCard>
  }

  renderRegularPaymentAuthorized(activity) {
    const { purchase: { merchant } } = this.props;

    if (!merchant) return false;

    const description = this.formatMessage(i18n.regularPaymentAuthorizedDescription, {
      merchantDisplayName: merchant.displayName,
      paymentNumber: activity.data.paymentNumber,
      totalOfPayments: activity.data.totalOfPayments,
      paymentAmount: format(activity.data.paymentAmount),
    });

    return <ActivityCard
      key={activity.id}
      type={activity.type}
      title={this.formatMessage(i18n.regularPaymentAuthorizedTitle)}
      time={activity.occurredAt}
    ><p>{description}</p></ActivityCard>
  }

  renderLastPaymentAuthorized(activity) {
    const { purchase: { merchant } } = this.props;

    if (!merchant) return false;

    const description = this.formatMessage(i18n.lastPaymentAuthorizedDescription, {
      merchantDisplayName: merchant.displayName,
    });

    return <ActivityCard
      key={activity.id}
      type={activity.type}
      title={this.formatMessage(i18n.lastPaymentAuthorizedTitle)}
      time={activity.occurredAt}
    ><p>{description}</p></ActivityCard>
  }

  renderMemberJoined(activity) {
    const { data: { memberUserFullName, memberUserEmail }} = activity;

    const user = {
      email: memberUserEmail,
      fullName: memberUserFullName,
    }

    const description = this.formatMessage(i18n.memberJoinedDescription, {
      fullName: this.userDisplayName(user),
    });

    return <ActivityCard
      key={activity.id}
      type={activity.type}
      title={this.formatMessage(i18n.memberJoinedTitle)}
      time={activity.occurredAt}
    ><p>{description}</p></ActivityCard>
  }

  renderMemberInvited(activity) {
    const { data: {
      invited
    }, attributes: {
      inviterUserId,
      inviterUserEmail,
      inviterUserFullName,
    } } = activity;

    const { organizerUser } = this.props;

    let user = {
      email: inviterUserEmail,
      fullName: inviterUserFullName
    };

    if (!inviterUserId) {
      user = organizerUser;
    }

    let description = this.formatMessage(i18n.memberInvitedDescription, {
      fullName: this.userDisplayName(user),
    });

    if (invited > 1) {
      description = description = this.formatMessage(i18n.memberInvitedDescriptionSeveral, {
        fullName: this.userDisplayName(user),
        invited,
      });
    }

    return <ActivityCard
      key={activity.id}
      type={activity.type}
      title={this.formatMessage(i18n.memberInvitedTitle)}
      time={activity.occurredAt}
    ><p>{description}</p></ActivityCard>
  }

  renderComment(activity) {
    const user = {
      id: activity.data.commenterUserId,
      fullName: activity.data.commenterUserFullName,
      email: activity.data.commenterUserEmail,
      avatarUrl: activity.data.commenterUserAvatarUrl,
    };

    return (
      <ActivityComment
        key={activity.id}
        title={this.userDisplayName(user)}
        comment={activity.data.commentText}
        time={activity.occurredAt}
        src={user.avatarUrl}
        fullName={user.fullName || user.email}
        userId={user.id}
      />
    );
  }

  renderActivities(activities) {
    return activities.map((activity) => {
      switch (activity.type) {
        case MEMBER_WITHDREW:
          return this.renderMemberWithdrew(activity);
        case PURCHASE_UPDATED:
          return this.renderPurchaseUpdated(activity);
        case PURCHASE_CLAIMED:
          return this.renderPurchaseClaimed(activity);
        case PURCHASE_TIPPED:
          return this.renderPurchaseTipped(activity);
        case PURCHASE_UNTIPPED:
          return this.renderPurchaseUntipped(activity);
        case PURCHASE_COMPLETED:
          return this.renderPurchaseCompleted(activity);
        case PAYMENT_CAPTURED:
          return this.renderPaymentCaptured(activity);
        case PAYMENT_AUTHORIZED:
        case PAYMENT_AUTHORIZED_DEPOSIT:
        case PAYMENT_AUTHORIZED_REGULAR:
        case PAYMENT_AUTHORIZED_LAST:
          return this.renderPaymentAuthorized(activity);
        case MEMBER_JOINED:
          return this.renderMemberJoined(activity);
        case MEMBER_INVITED:
          return this.renderMemberInvited(activity);
        case PURCHASE_COMMENT_CREATED:
          return this.renderComment(activity);
        default:
          return false;
      }
    });
  }

  renderActivityGroups() {
    const { purchase: { activities } } = this.props;
    if (!activities) return false;

    const groups = Object.keys(activities)
      .filter(key => activities[key].length)
      .map((key) => {
        return (
          <div className="activity-group" key={key}>
            <p className="date">
              {formatDatetimeShort(key)}
            </p>
            <div className="activities">
              {this.renderActivities(activities[key])}
            </div>
          </div>
        );
      });

    return groups;
  }

  renderForm() {
    const { handleSubmit, currentUser } = this.props;

    if (!currentUser) {
      return (
        <div className="create-account mt-2 mb-2">
          <Banner text={this.formatMessage(i18n.addCommentLoggedOutBanner)} />
          <div className="row mt-4">
            <div className="col-9">
              <p className="mt-3">
                {this.formatMessage(i18n.addCommentLoggedOutCopy)}
              </p>
            </div>
            <div className="col-3">
              <PrimaryButton
                onClick={this.onSignupClick}
              >{this.formatMessage(i18n.createAccount)}</PrimaryButton>
            </div>
          </div>
        </div>
      );
    }

    return (
      <form className="mt-4" onSubmit={handleSubmit(this.onSubmit)}>
        <AddComment
          hint={this.formatMessage(i18n.addCommentHint)}
          ctaLabel={this.formatMessage(i18n.postComment)}
          textLabel={this.formatMessage(i18n.addCommentPlaceholder)}
          userId={currentUser.id}
          fullName={currentUser.fullName}
          avatarSrc={currentUser.avatarUrl}
          value={this.state.commentText}
          error={this.state.error}
          onChange={(e) => {
            this.setState({
              commentText: e.target.value,
              error: null,
            });
          }}
        />
      </form>
    )
  }

  render() {
    return (
      <div className="purchase-activities container mt-5">
        <div className="container">
          <Subheader
            text={this.formatMessage(i18n.title)}
            anchor="nav-activities"
          />
          {this.renderForm()}
          <hr />
          {this.renderActivityGroups()}
        </div>
      </div>
    )
  }
}

const FORM_NAME = 'addActivityComment';

export default injectIntl(reduxForm({
  form: FORM_NAME,
})(Activities));



// WEBPACK FOOTER //
// ./src/desktop/components/purchase-dashboard/activities.js